// ============================================================
// SNAPSHOT SCHEDULER — Fase 9.2
// Agendamento automático de snapshots por câmera
// ============================================================
import { supabase } from '@/lib/supabase'
import type { CameraDevice, CameraSnapshot } from '@/types/vision'
import { obs } from '@/core/observability'

export interface SnapshotSchedule {
  cameraId:        string
  intervalMs:      number   // default: 5 min
  onSnapshot?:     (snapshot: CameraSnapshot) => void
}

const activeSchedules: Map<string, ReturnType<typeof setInterval>> = new Map()

export function scheduleCamera(cfg: SnapshotSchedule): () => void {
  const { cameraId, intervalMs, onSnapshot } = cfg
  cancelSchedule(cameraId)

  const timer = setInterval(async () => {
    const snap = await captureSnapshot(cameraId, 'scheduled')
    if (snap && onSnapshot) onSnapshot(snap)
  }, intervalMs) as unknown as ReturnType<typeof setInterval>

  activeSchedules.set(cameraId, timer)

  obs.debug('snapshot_scheduler', `Scheduled camera ${cameraId} every ${intervalMs / 1000}s`)

  return () => cancelSchedule(cameraId)
}

export function cancelSchedule(cameraId: string): void {
  const timer = activeSchedules.get(cameraId)
  if (timer) { clearInterval(timer); activeSchedules.delete(cameraId) }
}

export function cancelAllSchedules(): void {
  activeSchedules.forEach((t, id) => {
    clearInterval(t)
    activeSchedules.delete(id)
  })
}

// ─── Capture one snapshot ─────────────────────────────────────
export async function captureSnapshot(
  cameraId: string,
  trigger:  CameraSnapshot['trigger'] = 'manual',
): Promise<CameraSnapshot | null> {
  // Get camera info
  const { data: cam, error: camErr } = await supabase
    .from('camera_devices')
    .select('id, obra_id, snapshot_url, status')
    .eq('id', cameraId)
    .single()

  if (camErr || !cam) {
    obs.warn('snapshot_scheduler', `Camera ${cameraId} not found`)
    return null
  }

  if (cam.status !== 'online') {
    obs.debug('snapshot_scheduler', `Skipping snapshot — camera ${cameraId} is ${cam.status}`)
    return null
  }

  const storagePath = `${cam.obra_id}/${cam.id}/${Date.now()}.jpg`

  const { data, error } = await supabase
    .from('camera_snapshots')
    .insert({
      camera_id:     cameraId,
      obra_id:       cam.obra_id,
      storage_path:  storagePath,
      trigger,
      analysis_done: false,
    })
    .select()
    .single()

  if (error) {
    obs.error('snapshot_scheduler', `Insert failed: ${error.message}`)
    return null
  }

  obs.debug('snapshot_scheduler', `Snapshot ${data.id} captured for camera ${cameraId}`)
  return data as CameraSnapshot
}

// ─── Trigger incident snapshot for all obra cameras ──────────
export async function triggerIncidentSnapshot(obraId: string): Promise<void> {
  const { data: cameras } = await supabase
    .from('camera_devices')
    .select('id')
    .eq('obra_id', obraId)
    .eq('status', 'online')

  if (!cameras?.length) return

  await Promise.all(cameras.map(c => captureSnapshot(c.id, 'incident')))
  obs.info('snapshot_scheduler', `Incident snapshots triggered for ${cameras.length} cameras in obra ${obraId}`)
}

// ─── Get recent snapshots ────────────────────────────────────
export async function getRecentSnapshots(
  obraId: string,
  limit  = 20,
): Promise<CameraSnapshot[]> {
  const { data, error } = await supabase
    .from('camera_snapshots')
    .select('*')
    .eq('obra_id', obraId)
    .order('taken_at', { ascending: false })
    .limit(limit)

  if (error) { obs.warn('snapshot_scheduler', error.message); return [] }
  return (data ?? []) as CameraSnapshot[]
}
